import { defineStore } from "pinia";
import UserResource from "~/models/User";
import SettingResource from "~/models/schoology/Setting";
import SectionResource from "~/models/schoology/EspSection";
import ComparisonResource from "~/models/schoology/Comparison";
import SyncResource from "~/models/schoology/Sync";

const getSettingValue = (value) => {
  if (value === null || value === true || value === false || value === "") {
    return value;
  }
  if (!isNaN(Number(value))) {
    return Number(value);
  }

  return value;
};

export const useSchoologyStore = defineStore("schoology", {
  state: () => ({
    settings: [],
    check_types: [
      {
        name: "skip",
        label: "Skip",
        icon: "remove_circle",
        color: "primary",
        empty_color: "accent",
        error_level: 3,
      },
      {
        name: "error",
        label: "Errors",
        icon: "cancel",
        color: "negative",
        empty_color: "accent",
        error_level: 3,
      },
      {
        name: "remove",
        label: "Remove",
        icon: "delete",
        color: "warning",
        empty_color: "accent",
        error_level: 2,
      },
      {
        name: "create",
        label: "Create",
        icon: "add_circle",
        color: "info",
        empty_color: "accent",
        error_level: 2,
      },
      {
        name: "update",
        label: "Update",
        icon: "check_circle",
        color: "warning",
        empty_color: "accent",
        error_level: 2,
      },
      {
        name: "match",
        label: "Match",
        icon: "check_circle",
        color: "positive",
        empty_color: "accent",
        error_level: 2,
      },
    ],
    current_school_year: null,
    current_marking_periods: [],
    loading_syncs: false,
    latest_sync: null,
    my_syncs: [],
    default_sync_type: "mp_teacher",
    current_teacher: null,
    loading_courses: false,
    courses: [],
    loading_course: null,
    current_course: null,
    current_course_slgy_id: null,
    loading_course_comparison: null,
    current_course_comparison: null,
  }),
  getters: {
    is_admin: () => {
      const auth = useAuthStore();
      return auth.user.hasPermissionIn(["schoology-sync-admin"]);
      // auth.user.hasRoleIn(["god"]);
    },
    can_impersonate: () => {
      const auth = useAuthStore();
      return auth.user.hasPermissionIn([
        "schoology-sync-admin",
        "schoology-sync-impersonate",
      ]);
    },
    can_sync: () => {
      const auth = useAuthStore();
      return auth.user.hasPermissionIn([
        "schoology-sync-admin",
        "schoology-sync-edit",
      ]);
    },
    is_mismatch_teacher: (state) => {
      if (
        !state.current_teacher ||
        !state.current_course ||
        !state.current_course.main_session ||
        !state.current_course.main_session.primary_staff
      ) {
        return false;
      }
      return (
        state.current_teacher.username.toLowerCase() !==
        state.current_course.main_session.primary_staff.LOGIN_ID.toLowerCase()
      );
    },
  },
  actions: {
    setSettings(settings) {
      this.settings = settings;
      settings.forEach((setting) => {
        this[setting.name] = getSettingValue(setting.value);
      });
    },
    startListening() {
      const auth = useAuthStore();
      const { $echo } = useNuxtApp();
      //console.log("WS Listen SyncStatus");

      // Don't double listen
      $echo.private(`App.Models.User.${auth.user.id}`)
        .stopListening(".SyncStatusUpdated")
        .stopListening(".SyncStatusCreated");
      $echo.leave(`Schoology.SyncStatus`);

      // Clean listen to my syncs.
      $echo.private(`App.Models.User.${auth.user.id}`)
        .listen(".SyncStatusUpdated", (event) => {
          this.updateMySync(event.model);
        })
        .listen(".SyncStatusCreated", (event) => {
          this.updateMySync(event.model);
        });

      $echo.private(`Schoology.SyncStatus`)
        .listen(".SyncStatusUpdated", (event) => {
          const model = new SyncResource(event.model);
          if (model.updated_at > this.latest_sync.updated_at) {
            this.latest_sync = model;
          }
        })
        .listen(".SyncStatusCreated", (event) => {
          const model = new SyncResource(event.model);
          if (model.updated_at > this.latest_sync.updated_at) {
            this.latest_sync = model;
          }
        });
    },
    stopListening() {
      const auth = useAuthStore();
      const { $echo } = useNuxtApp();
      //console.log("WS Leave SyncStatus");
      $echo.private(`App.Models.User.${auth.user.id}`)
        .stopListening(".SyncStatusUpdated")
        .stopListening(".SyncStatusCreated");
      $echo.leave(`Schoology.SyncStatus`);
    },
    async loadConfig() {
      const auth = useAuthStore();
      const response = await SettingResource.all();
      this.setSettings(response.data);
      this.latest_sync = new SyncResource(response.latest_sync);
      this.current_school_year = response.current_school_year;
      this.current_marking_periods = response.current_marking_periods;
      // TODO: Do NOT HARD SET USER! /////////////////////////////
      //this.current_teacher = await UserResource.$find(21563); //auth.user; //new UserResource(response.current_teacher);
      this.current_teacher = auth.user; //new UserResource(response.current_teacher);
      //this.loadTeacherSections(this.current_teacher.username);
      this.loadSyncStatus();
    },
    async loadTeacher(user = null) {
      this.current_teacher = null;
      if (!user) {
        const auth = useAuthStore();
        user = auth.user;
      }
      //this.current_course = null;
      //this.current_course_comparison = null;
      const teacher_promise = UserResource.$find(user.id);
      const course_promise = this.loadTeacherSections(user.username);
      this.current_teacher = await teacher_promise;
      await course_promise;
    },
    async loadTeacherSections(username, markingPeriods = []) {
      if (!username) {
        username = this.current_teacher.username;
      }
      if (markingPeriods || markingPeriods.length == 0) {
        markingPeriods = this.current_marking_periods;
      }
      this.loading_courses = true;
      try {
        const courses = await SectionResource.with([
          "marking_periods",
          "sessions",
        ])
          .where("SCHOOL_YEAR", this.current_school_year)
          .where("active_teacher", username)
          .where("allowed_schoology_sync", 1)
          .where("receives_marks", 1)
          .whereIn("in_marking_periods", markingPeriods)
          .$get();
        courses.sort((a, b) => {
          return a.sort_key.toLocaleString() > b.sort_key.toLocaleString()
            ? 1
            : -1;
        });
        this.courses = courses;
      } catch (error) {
        console.log(error);
      }
      this.loading_courses = false;
    },
    async loadSection(sectionKey, force = false) {
      if (
        !force &&
        this.current_course &&
        this.current_course.SECTION_KEY == sectionKey
      ) {
        // Already have what we need.
        return;
      }
      this.loading_course = true;
      this.current_course = null;
      this.current_course_comparison = null;
      this.current_course_slgy_id = null;
      try {
        const response = await SectionResource.find(sectionKey);
        this.current_course = new SectionResource(response.data);
        this.current_course_comparison = new ComparisonResource(
          response.comparisons
        ).for(this.current_course);
        this.current_course_slgy_id = response.slgy[sectionKey].id;
        console.log(this.current_course_slgy_id);
        //console.log("loadedCourse", this);
      } catch (error) {
        console.log(error);
      }
      this.loading_course = false;
    },

    async loadSectionComparison(sectionKey) { //, assignmentId = null
      this.loading_course = true;
      this.current_course = null;
      this.current_course_comparison = null;
      this.current_course_slgy_id = null;
      try {
        const response = await SectionResource.find(sectionKey);
        this.current_course = new SectionResource(response.data);
        this.current_course_comparison = new ComparisonResource(
          response.comparisons
        ).for(this.current_course);
      } catch (error) {
        console.log(error);
      }
      this.loading_course = false;
    },

    async loadSyncStatus() {
      this.loading_syncs = true;
      try {
        const auth = useAuthStore();

        const response = await SyncResource.where(
          "requested_by_id",
          auth.user.id
        )
          .limit(5)
          .with("requested_by")
          .orderBy("-created_at")
          .get();
        this.my_syncs = response.data;
      } catch (error) {
        console.log(error);
      }
      this.loading_syncs = false;
    },

    async loadLatestSystemSync() {
      try {
        this.latest_sync = await SyncResource.where("is_system_sync", 1)
          .limit(1)
          .with("requested_by")
          .orderBy("-created_at")
          .$first();
      } catch (error) {
        console.log(error);
      }
    },

    async performSync(syncStatusForm) {
      try {
        const response = await syncStatusForm.value.save();
        //console.log("performSync-response", response);
        useApiResponseHandler().success('Sync request successful.');

        this.updateMySync(response.data);

        if (this.current_course && this.current_course.SECTION_KEY) {
          this.loadSection(this.current_course.SECTION_KEY);
        }
      } catch (error) {
        if (!error.response) {
          console.log(error);
        } else {
          useApiResponseHandler().error(error);
        }
      }
    },

    updateRelatedData(model, myKey, matchFn) {
      if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
        // Don't care about save.
        return;
      }

      const dataIndex = this[myKey].findIndex(matchFn);
      if (dataIndex == -1) {
        this[myKey].push(model);
      } else {
        this[myKey].splice(dataIndex, 1, model);
      }
    },
    updateRelatedDataReverse(model, myKey, matchFn) {
      if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
        // Don't care about save.
        return;
      }

      const dataIndex = this[myKey].findIndex(matchFn);
      if (dataIndex == -1) {
        this[myKey].unshift(model);
      } else {
        this[myKey].splice(dataIndex, 1, model);
      }
    },
    updateMySync(model) {
      model = new SyncResource(model);
      const dataIndex = this.my_syncs.findIndex((i) => i.id == model.id);
      if (dataIndex == -1) {
        this.my_syncs.unshift(model);
      } else if (model.updated_at > this.my_syncs[dataIndex].updated_at) {
        this.my_syncs.splice(dataIndex, 1, model);
      }
      // else, we already have a newer object.
    },
  },
});
